import TextField, { TextFieldProps } from '@mui/material/TextField';
import { AppImage, brandclub_colors, useStacklineTheme } from '@stackline/ui';
import React, { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { withRouter } from 'react-router-dom';
import { handleSearchChange } from 'src/components/Header/HeaderSearchContainer';
import { AiSuggestionIcon } from 'src/components/SvgIcons/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';
import {
  useMainEntity,
  useMainEntityType,
  useRetailerDisplayName,
  useTimePeriods,
  useUserEmail
} from 'src/utils/Hooks/reduxSelectors';
import { buildCustomDateRangeDisplayName } from 'src/utils/dateformatting';
import { getAdvisorHost, getAppName, getQueryParamValue, shouldShowBeaconAdvisor } from 'src/utils/app';
import { AppName } from 'sl-api-connector/types';
import { interpolateQuestion, mapUrlParamsToFullNames } from 'src/utils/advisorSuggestedQuestions';
import { useAdvisorSuggestedQuestions } from 'src/services/advisorSuggestedQuestions';
import { useAdvisorSuggestedQuestionsStore } from 'src/zustandStores/useAdvisorSuggestedQuestionsStore';
import { extractAdvisorFilterIds } from 'src/services/advisorContext';
import { track } from 'src/utils/mixpanel';
import './SearchInputBar.css';

export const SearchTextField = ({
  sx,
  endAdornment,
  customWidth,
  ...rest
}: TextFieldProps & {
  endAdornment?: React.ReactNode;
  customWidth?: number;
}) => {
  const theme = useStacklineTheme();
  // Adjust right padding: 4px when endAdornment present (for 4px gap to button), 14px otherwise
  const rightPadding = endAdornment ? 4 : 14;

  return (
    <TextField
      variant="standard"
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <AppImage
            src="/svg/icons/search.svg"
            alt="search-icon"
            style={{
              marginRight: 8,
              height: 16,
              width: 16,
              opacity: 0.45
            }}
          />
        ),
        endAdornment,
        autoComplete: 'off',
        id: 'type-search'
      }}
      sx={{
        ...{
          borderRadius: 15,
          border: `1px solid ${theme.colors.primaryGray}`,
          height: 32,
          width: customWidth ?? 290,
          boxSizing: 'border-box',
          backgroundColor: brandclub_colors.white,
          '& div.MuiInput-root': {
            padding: `0 ${rightPadding}px 0 12px`,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            boxSizing: 'border-box',
            '& input': {
              padding: 0,
              fontSize: '14px',
              fontFamily: 'Roboto',
              color: theme.colors.primary,
              flex: 1
            }
          },
          '.MuiInput-input::placeholder': {
            color: theme.colors.secondary,
            fontSize: '14px'
          }
        },
        ...sx
      }}
      {...rest}
    />
  );
};

const NewSearchInput: FC<any> = ({ history }) => {
  const [input, setInput] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { pathname } = window.location;
  const appName = getAppName();
  const theme = useStacklineTheme();

  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const retailerDisplayName = useRetailerDisplayName();
  const retailer = useAppSelector((state) => state.retailer);
  const filters = useAppSelector((state) => state.filters);

  const showAdvisorButton = appName === AppName.Atlas || (appName === AppName.Beacon && shouldShowBeaconAdvisor());

  // Company-specific session email for personalized questions lookup
  const userEmail = useUserEmail();

  // Entity type from URL path (e.g. /company/..., /category/..., /brand/...) via Redux
  const entityType = useMainEntityType();
  // Full entity object for dynamic question interpolation (e.g. {{entityName}} -> "Bakeware")
  const mainEntity = useMainEntity();

  // "All Categories" (/category/0) gets its own DynamoDB record so the PM can
  // configure suggested questions specifically for the portfolio-level view.
  const effectiveEntityType = useMemo(() => {
    if (entityType === 'category' && mainEntity && String(mainEntity.id) === '0') {
      return 'allcategories';
    }
    return entityType;
  }, [entityType, mainEntity]);

  // Atlas Advisor suggested questions: tab/subtab from URL -> mapping (tab, subtab, tabFullName, subTabFullName) -> API
  const tabParam = getQueryParamValue('tab', '');
  const subtabParam = getQueryParamValue('subtab', '');
  const mapping = useMemo(
    () => (tabParam && subtabParam ? mapUrlParamsToFullNames(tabParam, subtabParam) : null),
    [tabParam, subtabParam]
  );
  const setContext = useAdvisorSuggestedQuestionsStore((s) => s.setContext);
  useEffect(() => {
    if (mapping) {
      setContext({
        tab: mapping.tab,
        subtab: mapping.subtab,
        tabFullName: mapping.tabFullName,
        subTabFullName: mapping.subTabFullName,
        entityType: effectiveEntityType || null
      });
    } else {
      setContext({ tab: null, subtab: null, tabFullName: null, subTabFullName: null, entityType: null });
    }
  }, [mapping, setContext, effectiveEntityType]);
  const { data: suggestedQuestions = [] } = useAdvisorSuggestedQuestions(
    effectiveEntityType || undefined,
    tabParam || undefined,
    subtabParam || undefined,
    userEmail || undefined
  );

  // Cache the advisor host since hostname doesn't change during session
  const advisorHost = useMemo(() => getAdvisorHost(), []);

  useEffect(() => {
    if (pathname !== '/search') {
      setInput('');
    }
  }, [pathname]);

  // Dedup guard: prevent duplicate Mixpanel events from double-clicks or edge cases.
  // Reset when the page context changes so the same question can be tracked on a different page.
  const lastTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    lastTrackedRef.current = null;
  }, [tabParam, subtabParam, mainEntity?.name]);

  /**
   * Opens the Advisor application in a new tab.
   * If search text is provided, it will be passed as a prompt parameter.
   * Filter IDs (retailers, categories, subcategories, segments) are passed
   * as URL params so zenith-ui can pre-populate its filter modal.
   * Only triggered by click - not by keyboard events.
   *
   * Note: userEmail is NOT passed via URL. zenith-ui gets the user's email
   * from its own Cognito auth session, which is more secure than URL params.
   */
  const handleAskAdvisor = (promptText?: string) => {
    const text = promptText !== undefined ? promptText.trim() : input.trim();

    const fingerprint = `${text}|${tabParam}|${subtabParam}|${mainEntity?.name}`;
    if (lastTrackedRef.current !== fingerprint) {
      track('Ask Advisor Search Bar', {
        entityName: mainEntity?.name || '',
        entityType: entityType || '',
        tab: tabParam,
        subtab: subtabParam,
        tabFullName: mapping?.tabFullName || '',
        subtabFullName: mapping?.subTabFullName || '',
        email: userEmail || '',
        question: text
      });
      lastTrackedRef.current = fingerprint;
    }

    const params = new URLSearchParams();
    if (text) {
      params.set('prompt', text);
    }

    // Identify which product the filters are coming from (atlas or beacon)
    const source = appName === AppName.Beacon ? 'beacon' : 'atlas';
    params.set('source', source);

    // Extract filter IDs from mainEntity + Atlas filter modal (Redux state.filters)
    const { retailerIds, categoryIds, subcategoryIds, segments } = extractAdvisorFilterIds(
      retailer,
      mainEntity,
      filters
    );

    if (retailerIds.length > 0) params.set('retailers', retailerIds.join(','));
    if (categoryIds.length > 0) params.set('categories', categoryIds.join(','));
    if (subcategoryIds.length > 0) params.set('subcategories', subcategoryIds.join(','));
    if (segments.length > 0) params.set('segments', segments.join(','));

    const queryString = params.toString();
    const advisorUrl = queryString ? `https://${advisorHost}/?${queryString}` : `https://${advisorHost}/`;
    window.open(advisorUrl, '_blank', 'noopener,noreferrer');
  };

  // Ask Advisor button as endAdornment (only for Atlas with feature flag)
  // Figma 8746-92882: text-only pill, 24px height, 12px horizontal padding, 13px radius
  // Border: 1px solid #E8E8ED, font-weight 500 (medium), rainbow rotating border on hover
  const askAdvisorButton = showAdvisorButton ? (
    <span
      style={{
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Rainbow border wrapper - the gradient rotates around the button */}
      <span className="ask-advisor-btn-wrapper">
        <button
          type="button"
          className="ask-advisor-btn-inner"
          onClick={() => handleAskAdvisor()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3px 12px',
            borderRadius: 13,
            border: 'none',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: 'Roboto',
            fontWeight: 500,
            color: '#031425',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            whiteSpace: 'nowrap',
            height: 22, // 24 - 2px for gradient border (1px each side)
            flexShrink: 0,
            boxSizing: 'border-box',
            lineHeight: '20px',
            transition: 'background-color 0.15s ease'
          }}
        >
          Ask Advisor
        </button>
      </span>
    </span>
  ) : undefined;

  // Extra tokens for question interpolation (non-entity values like time period).
  // "Custom Date" → build from startDayId/endDayId for guaranteed capitalisation,
  const extraTokens = useMemo(() => {
    // Build human-readable label for main time period
    const short = mainTimePeriod?.shortDisplayName || '';
    let timePeriodLabel: string;
    if (mainTimePeriod.id === 'cd') {
      timePeriodLabel =
        mainTimePeriod?.startDayId && mainTimePeriod?.endDayId
          ? buildCustomDateRangeDisplayName(mainTimePeriod.startDayId, mainTimePeriod.endDayId)
          : mainTimePeriod?.displayName || short;
    } else if (short.toLowerCase() === 'ytd') {
      timePeriodLabel = 'YTD';
    } else {
      timePeriodLabel = short.toLowerCase();
    }

    // Build human-readable label for comparison time period
    const compShort = comparisonTimePeriod?.shortDisplayName || '';

    return {
      mainTimePeriod: timePeriodLabel + ' period',
      comparisonTimePeriod: compShort.toLowerCase(),
      retailerName: retailerDisplayName || ''
    };
  }, [
    retailerDisplayName,
    mainTimePeriod.id,
    mainTimePeriod.startDayId,
    mainTimePeriod.endDayId,
    mainTimePeriod.displayName,
    mainTimePeriod.shortDisplayName,
    comparisonTimePeriod?.shortDisplayName
  ]);

  // Interpolate {{token}} placeholders in questions with values from the current entity
  // and extra context (e.g. {{entityName}} -> "Bakeware", {{mainTimePeriod}} -> "past 13 weeks").
  const resolvedQuestions = useMemo(
    () => suggestedQuestions.map((q) => interpolateQuestion(q, mainEntity, extraTokens)),
    [suggestedQuestions, mainEntity, extraTokens]
  );

  // Suggested questions dropdown on focus when input is empty; width is fixed 600px when advisor shown (no expand).
  const trimmedInput = input.trim().toLowerCase();
  const showSuggestions =
    showAdvisorButton && isSearchFocused && trimmedInput.length === 0 && resolvedQuestions.length > 0;
  const searchWidth = showAdvisorButton ? 600 : 290;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom,
      left: rect.left,
      width: rect.width
    });
  }, []);

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    updateDropdownPosition();
  };
  const handleSearchBlur = () => {
    setTimeout(() => setIsSearchFocused(false), 200);
  };
  const handleSuggestionMouseDown = (question: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    handleAskAdvisor(question);
    setIsSearchFocused(false);
  };

  return (
    <div
      ref={wrapperRef}
      className={`search-input-bar-wrapper${showSuggestions ? ' search-input-bar-wrapper--open' : ''}`}
      style={{ width: searchWidth }}
    >
      <SearchTextField
        variant="standard"
        value={input}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsSearchFocused(false);
            // TODO: fix types, verify functionality
            // @ts-ignore
            handleSearchChange(input, -1, appName, retailer, mainTimePeriod, history.push);
          }
        }}
        onChange={(event) => setInput(event.target.value)}
        endAdornment={askAdvisorButton}
        customWidth={searchWidth}
        sx={{
          borderColor: '#e8e8ed',
          backgroundColor: '#ffffff',
          ...(showSuggestions
            ? {
                borderRadius: '15px 15px 0 0',
                borderBottomColor: 'transparent'
              }
            : {})
        }}
      />
      {showSuggestions &&
        createPortal(
          <div
            className="advisor-suggested-questions-dropdown"
            role="list"
            style={dropdownStyle}
          >
            {resolvedQuestions.map((question, index) => (
              <button
                key={index}
                type="button"
                className="advisor-suggested-question-item"
                onMouseDown={handleSuggestionMouseDown(question)}
                role="listitem"
              >
                <AiSuggestionIcon className="advisor-suggested-question-icon" style={{ width: 12, height: 12 }} />
                <span>{question}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default withRouter(NewSearchInput) as unknown as React.ComponentType;
